import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { submitLead, errorMessage } from "../../lib/api";
import { Field, SelectField, inputCls } from "./Field";
import { LocationPicker } from "./LocationPicker";
import { INDUSTRIES, SERVICE_TYPES, SHIFTS, FACILITIES } from "./data";
import { SuccessCard } from "./SuccessCard";
import { PhoneVerify } from "./PhoneVerify";
import { EMPTY_KYC, KycSection, kycMissing, kycPayload } from "./KycSection";
import { RolePicker } from "./RolePicker";
import { SkillChips } from "./SkillChips";
import { AcceptTerms } from "./AcceptTerms";

const EMPTY = {
  company_name: "", gst_number: "", hr_name: "", phone: "", full_address: "", state: "", district: "",
  industry: "", service_type: "", department: "", job_role: "", headcount: "", shift: "", pay_type: "monthly", pay_amount: "", required_skills: [], facilities: [], phone_verification_token: "", kyc: EMPTY_KYC, accepted_terms: false, declaration: false,
};

const DECLARATION = "I declare that I am an authorised representative of the company / organisation named above, that the hiring requirement submitted is genuine and for our own company or organisation, and that all details and KYC documents provided are true and correct. I agree to pay wages, statutory dues and the agreed commission for employees hired through Balangir Group.";

const Declaration = ({ checked, onChange }) => (
  <label data-testid="employer-declaration" className="flex cursor-pointer items-start gap-3 rounded-lg border border-orange-500/40 bg-orange-500/5 p-4 text-sm leading-relaxed text-slate-300 md:col-span-2">
    <Checkbox data-testid="employer-declaration-checkbox" checked={checked} onCheckedChange={(v) => onChange(!!v)}
      className="mt-0.5 rounded-none border-slate-500 data-[state=checked]:border-orange-500 data-[state=checked]:bg-orange-500" />
    <span><span className="font-semibold text-white">Employer Declaration: </span>{DECLARATION}</span>
  </label>
);

const REQUIRED = { state: "State", district: "District", industry: "Industry", service_type: "Service type", department: "Department", job_role: "Job role", shift: "Shift" };

export const EmployerForm = ({ prefill, active, onContext }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (active) onContext({ tab: "employer", job_role: f.job_role, headcount: f.headcount, state: f.state, district: f.district });
  }, [active, onContext, f.job_role, f.headcount, f.state, f.district]);
  useEffect(() => {
    if (!prefill) return;
    setDone(null);
    setF((p) => ({ ...p, department: prefill.department, job_role: prefill.job_role, required_skills: p.department === prefill.department ? p.required_skills : [] }));
  }, [prefill]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggleFacility = (v) =>
    setF((p) => ({ ...p, facilities: p.facilities.includes(v) ? p.facilities.filter((x) => x !== v) : [...p.facilities, v] }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = Object.keys(REQUIRED).find((k) => !f[k]);
    if (missing) return toast.error(`Please select ${REQUIRED[missing]}`);
    const kycErr = kycMissing(f.kyc);
    if (kycErr) return toast.error(kycErr);
    if (!f.declaration) return toast.error("Please accept the Employer Declaration");
    if (!f.accepted_terms) return toast.error("Please accept the Legal Disclaimer, Terms and Privacy Policy");
    setBusy(true);
    try {
      const { declaration, ...rest } = f;
      const res = await submitLead("employer", {
        ...rest, headcount: Number(f.headcount), pay_amount: Number(f.pay_amount), phone_verification_token: f.phone_verification_token || null,
        kyc: kycPayload(f.kyc),
      });
      setDone(res);
      setF(EMPTY);
      toast.success("Requirement submitted. Our team will call your HR contact.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <SuccessCard kind="employer" reference={done.reference} onReset={() => setDone(null)} />;

  return (
    <form onSubmit={onSubmit} data-testid="employer-form" className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field label="Company / Firm / Mall Name">
        <Input required data-testid="employer-input-company-name" className={inputCls} value={f.company_name}
          onChange={(e) => set("company_name")(e.target.value)} placeholder="e.g. Shree Textiles Pvt Ltd" />
      </Field>
      <Field label="GST Number" hint="optional">
        <Input data-testid="employer-input-gst" className={`${inputCls} uppercase`} maxLength={15} value={f.gst_number}
          onChange={(e) => set("gst_number")(e.target.value)} placeholder="15-character GSTIN" />
      </Field>
      <Field label="HR Contact Name">
        <Input required data-testid="employer-input-hr-name" className={inputCls} value={f.hr_name}
          onChange={(e) => set("hr_name")(e.target.value)} placeholder="Full name" />
      </Field>
      <PhoneVerify prefix="employer" label="HR Phone" phone={f.phone} token={f.phone_verification_token}
        onPhone={(v) => setF((p) => ({ ...p, phone: v, phone_verification_token: "" }))} onToken={set("phone_verification_token")} />
      <LocationPicker prefix="employer" state={f.state} district={f.district}
        onChange={(s, d) => setF((p) => ({ ...p, state: s, district: d }))} />
      <div className="md:col-span-2">
        <Field label="Full Site / Office Address" hint="building, street, area, city, PIN">
          <Textarea required minLength={10} maxLength={300} data-testid="employer-input-address" className={`${inputCls} h-20`} value={f.full_address}
            onChange={(e) => set("full_address")(e.target.value)} placeholder="e.g. Plot 21, SIPCOT Industrial Estate, Perundurai, Erode - 638052" />
        </Field>
      </div>
      <SelectField label="Industry Sector" value={f.industry} onChange={set("industry")} options={INDUSTRIES} testId="employer-select-industry" />
      <SelectField label="Service Requirement" value={f.service_type} onChange={set("service_type")} options={SERVICE_TYPES} testId="employer-select-service-type" />
      <RolePicker prefix="employer" roleLabel="Role Needed" department={f.department} jobRole={f.job_role}
        onChange={(d, r) => setF((p) => ({ ...p, department: d, job_role: r, required_skills: d === p.department ? p.required_skills : [] }))} />
      <SkillChips prefix="employer" label="Required Skills" hint="optional, improves matching" department={f.department}
        value={f.required_skills} onChange={set("required_skills")} />
      <Field label="Headcount Needed">
        <Input required type="number" min={1} max={10000} data-testid="employer-input-headcount" className={inputCls}
          value={f.headcount} onChange={(e) => set("headcount")(e.target.value)} placeholder="e.g. 50" />
      </Field>
      <SelectField label="Shift Details" value={f.shift} onChange={set("shift")} options={SHIFTS} testId="employer-select-shift" />
      <SelectField label="Pay Structure" value={f.pay_type} onChange={set("pay_type")} testId="employer-select-pay-type"
        options={[{ value: "monthly", label: "Monthly Salary" }, { value: "daily", label: "Daily Wage" }]} />
      <Field label={f.pay_type === "daily" ? "Daily Wage (₹ per head)" : "Monthly Salary (₹ per head)"}>
        <Input required type="number" min={1} data-testid="employer-input-pay-amount" className={inputCls}
          value={f.pay_amount} onChange={(e) => set("pay_amount")(e.target.value)} placeholder={f.pay_type === "daily" ? "e.g. 650" : "e.g. 16000"} />
      </Field>
      <div className="md:col-span-2">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">Statutory & Facilities</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FACILITIES.map((x) => (
            <label key={x.value} className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/15 bg-[#0F172A] px-3 py-2.5 text-sm text-slate-300 transition-colors hover:border-orange-500/60">
              <Checkbox data-testid={`employer-checkbox-${x.id}`} checked={f.facilities.includes(x.value)}
                onCheckedChange={() => toggleFacility(x.value)} className="rounded-none border-slate-500 data-[state=checked]:border-orange-500 data-[state=checked]:bg-orange-500" />
              {x.label}
            </label>
          ))}
        </div>
      </div>
      <KycSection prefix="employer" employer kyc={f.kyc} setKyc={(fn) => setF((p) => ({ ...p, kyc: fn(p.kyc) }))} />
      <Declaration checked={f.declaration} onChange={set("declaration")} />
      <AcceptTerms prefix="employer" checked={f.accepted_terms} onChange={set("accepted_terms")} />
      <SubmitButton busy={busy} testId="employer-submit-button" label="Submit Workforce Requirement" />
    </form>
  );
};

export const SubmitButton = ({ busy, testId, label }) => (
  <div className="md:col-span-2">
    <button type="submit" disabled={busy} data-testid={testId}
      className="group inline-flex h-12 w-full items-center justify-center gap-2 bg-orange-500 px-8 font-semibold text-white transition-colors hover:bg-orange-600 hover:text-white disabled:opacity-60 sm:w-auto">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </button>
  </div>
);
